// File: AccountingDashboard.tsx (REFACTORED VERSION)
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  FaMoneyBillWave, FaChartLine, FaChartPie, FaChartBar, 
  FaArrowUp, FaArrowDown, FaCalendarAlt,
  FaDollarSign, FaCreditCard, FaCashRegister, FaUserTie,
  FaCheckCircle, FaClock, FaTimesCircle, FaWallet, FaReceipt, FaCircle,
  FaBusinessTime, FaBalanceScale, FaRegMoneyBillAlt,
  FaPercentage, FaChartArea, FaTable, FaListAlt,
  FaExchangeAlt, FaTachometerAlt, FaHistory, 
} from 'react-icons/fa';
import {
  FaArrowTrendUp,
  FaArrowTrendDown,
  FaCalendarWeek,
  FaShieldCat,
  FaFileInvoiceDollar,
  FaTags,
  FaCalendarCheck
} from 'react-icons/fa6';

import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { formatCurrency } from '../../../../utils/formatCurrency';
import {
  getAccountingRecords,
  getAccountingCategories,
  getVendors,
  getAccountingRecordsByDateRange,
  Accounting,
  AccountingCategory,
  Vendor,
  cacheKeys
} from '../../../../firebase/accountingFirestoreService';
import { cache } from '../../../../utils/cacheManager';
import { useDashboardFilters } from '../../../../contexts/DashboardFilterContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Import components
import StatCard from '../accounting/StatCard';
import CustomTooltip from '../accounting/CustomTooltip';
import RecentTransactionsTable from '../accounting/RecentTransactionsTable';
import TopTransactionsList from '../accounting/TopTransactionsList';
import ExportControls from '../accounting/ExportControls';
import DashboardFilters from '../accounting/DashboardFilters';

interface TimeFrame {
  label: string;
  value: 'week' | 'month' | 'year' | 'quarter' | 'custom';
}

const AccountingDashboard: React.FC = () => {
  const [records, setRecords] = useState<Accounting[]>([]);
  const [categories, setCategories] = useState<AccountingCategory[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTimeFrame, setActiveTimeFrame] = useState<TimeFrame['value']>('year');
  const [showFilters, setShowFilters] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { month, year, monthFrom, monthTo } = useDashboardFilters();

  // Fetch data with filters
  useEffect(() => {
    fetchDashboardData();
  }, [month, year, monthFrom, monthTo, activeTimeFrame]);

  const fetchDashboardData = async (forceInvalidate: boolean = false) => {
    setLoading(true);
    const todayStr = new Date().toISOString().split('T')[0];
    if (forceInvalidate) {
      try {
        cache.invalidate(cacheKeys.recordsAll);
        cache.invalidate(cacheKeys.categories);
        cache.invalidate(cacheKeys.vendors);

        // invalidate common ranges used by the UI
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);
        const lastWeekStr = lastWeek.toISOString().split('T')[0];
        cache.invalidate(cacheKeys.recordsRange(lastWeekStr, todayStr));

        const firstDay = new Date();
        firstDay.setDate(1);
        const firstDayStr = firstDay.toISOString().split('T')[0];
        cache.invalidate(cacheKeys.recordsRange(firstDayStr, todayStr));

        const quarterStart = new Date();
        const currentQuarter = Math.floor(quarterStart.getMonth() / 3);
        quarterStart.setMonth(currentQuarter * 3, 1);
        const quarterStartStr = quarterStart.toISOString().split('T')[0];
        cache.invalidate(cacheKeys.recordsRange(quarterStartStr, todayStr));

        const yearStart = new Date(new Date().getFullYear(), 0, 1);
        const yearStartStr = yearStart.toISOString().split('T')[0];
        cache.invalidate(cacheKeys.recordsRange(yearStartStr, todayStr));

        if (customDateRange.start && customDateRange.end) {
          cache.invalidate(cacheKeys.recordsRange(customDateRange.start, customDateRange.end));
        }
      } catch (err) {
        console.warn('Cache invalidation error', err);
      }
    }
    try {
      let filteredRecords: Accounting[] = [];
      const todayStr = new Date().toISOString().split('T')[0];

      // Apply filters based on selected time frame
      switch (activeTimeFrame) {
        case 'week':
          const lastWeek = new Date();
          lastWeek.setDate(lastWeek.getDate() - 7);
          const lastWeekStr = lastWeek.toISOString().split('T')[0];
          const weekResult = await cache.get(
            cacheKeys.recordsRange(lastWeekStr, todayStr),
            () => getAccountingRecordsByDateRange(lastWeekStr, todayStr),
            30 * 60 * 1000
          );
          if (weekResult.success) filteredRecords = weekResult.data || [];
          break;
          
        case 'month':
          const firstDay = new Date();
          firstDay.setDate(1);
          const firstDayStr = firstDay.toISOString().split('T')[0];
          const monthResult = await cache.get(
            cacheKeys.recordsRange(firstDayStr, todayStr),
            () => getAccountingRecordsByDateRange(firstDayStr, todayStr),
            30 * 60 * 1000
          );
          if (monthResult.success) filteredRecords = monthResult.data || [];
          break;
          
        case 'quarter':
          const quarterStart = new Date();
          const currentQuarter = Math.floor(quarterStart.getMonth() / 3);
          quarterStart.setMonth(currentQuarter * 3, 1);
          const quarterStartStr = quarterStart.toISOString().split('T')[0];
          const quarterResult = await cache.get(
            cacheKeys.recordsRange(quarterStartStr, todayStr),
            () => getAccountingRecordsByDateRange(quarterStartStr, todayStr),
            30 * 60 * 1000
          );
          if (quarterResult.success) filteredRecords = quarterResult.data || [];
          break;
          
        case 'year':
          const yearStart = new Date(new Date().getFullYear(), 0, 1);
          const yearStartStr = yearStart.toISOString().split('T')[0];
          const yearResult = await cache.get(
            cacheKeys.recordsRange(yearStartStr, todayStr),
            () => getAccountingRecordsByDateRange(yearStartStr, todayStr),
            30 * 60 * 1000
          );
          if (yearResult.success) filteredRecords = yearResult.data || [];
          break;
          
        case 'custom':
          if (customDateRange.start && customDateRange.end) {
            const customResult = await cache.get(
              cacheKeys.recordsRange(customDateRange.start, customDateRange.end),
              () => getAccountingRecordsByDateRange(customDateRange.start, customDateRange.end),
              30 * 60 * 1000
            );
            if (customResult.success) filteredRecords = customResult.data || [];
          }
          break;
          
        default:
            const allRecords = await cache.get(cacheKeys.recordsAll, () => getAccountingRecords(), 30 * 60 * 1000);
          if (allRecords.success) filteredRecords = allRecords.data || [];
      }
      
      // If date-range queries returned nothing, fall back to loading all records
      if ((!filteredRecords || filteredRecords.length === 0)) {
        try {
          console.warn('No records found for selected range — falling back to loading all records');
          const allRecords = await cache.get(cacheKeys.recordsAll, () => getAccountingRecords(), 30 * 60 * 1000);
          if (allRecords && allRecords.success) {
            filteredRecords = allRecords.data || [];
          }
        } catch (e) {
          console.error('Fallback to all records failed', e);
        }
      }

      setRecords(filteredRecords);
      
      // Fetch other data
      const [categoriesResult, vendorsResult] = await Promise.all([
        cache.get(cacheKeys.categories, () => getAccountingCategories(), 24 * 60 * 60 * 1000),
        cache.get(cacheKeys.vendors, () => getVendors(), 24 * 60 * 60 * 1000)
      ]);
      
      if (categoriesResult.success) setCategories(categoriesResult.data || []);
      if (vendorsResult.success) setVendors(vendorsResult.data || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Financial KPIs calculation
  const financialKPIs = useMemo(() => {
    const getCategoryType = (categoryName: string) => {
      const categoryType = categories.find(c => c.name === categoryName)?.type;
      if (categoryType) return categoryType;
      return categoryName.startsWith('Event Receipt') ? 'Income' : '';
    };

    // Get income categories
    const incomeCategories = categories.filter(c => c.type === 'Income').map(c => c.name);
    const expenseCategories = categories.filter(c => c.type === 'Expense').map(c => c.name);

    // Calculate based on category type
    const totalIncome = records
      .filter(r => incomeCategories.includes(r.category) || getCategoryType(r.category) === 'Income')
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);

    const totalExpense = records
      .filter(r => expenseCategories.includes(r.category) || getCategoryType(r.category) === 'Expense')
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);

    const totalRevenue = totalIncome - totalExpense;
    const profitMargin = totalIncome > 0 ? (totalRevenue / totalIncome) * 100 : 0;
    
    // Calculate cash flow
    const cashInflow = records
      .filter(r => ['Income', 'Transfer'].includes(getCategoryType(r.category)))
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);
    
    const cashOutflow = records
      .filter(r => ['Expense'].includes(getCategoryType(r.category)))
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);
    
    const cashFlow = cashInflow - cashOutflow;
    
    return {
      totalRevenue,
      totalIncome,
      totalExpense,
      netProfit: totalRevenue,
      profitMargin,
      avgTransaction: records.length > 0 ? (totalIncome + totalExpense) / records.length : 0,
      transactionCount: records.length,
      cashFlow,
      cashInflow,
      cashOutflow,
      avgExpense: expenseCategories.length > 0 ? 
        totalExpense / records.filter(r => expenseCategories.includes(r.category)).length : 0,
      avgIncome: incomeCategories.length > 0 ? 
        totalIncome / records.filter(r => incomeCategories.includes(r.category)).length : 0
    };
  }, [records, categories]);

  // Dashboard stats calculation
  const dashboardStats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const todayRecords = records.filter(r => r.date === today);
    const currentMonthRecords = records.filter(r => {
      const recordDate = new Date(r.date);
      return recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear;
    });
    
    const approved = records.filter(r => r.status === 'Approved');
    const pending = records.filter(r => r.status === 'Pending');
    const paid = records.filter(r => r.status === 'Paid');
    const rejected = records.filter(r => r.status === 'Rejected');
    
    // Calculate category distribution, including event receipt categories that may not exist in category settings.
    const categoryDistribution = records.reduce((acc, record) => {
      if (!acc[record.category]) {
        acc[record.category] = {
          amount: 0,
          count: 0,
          percentage: 0
        };
      }
      acc[record.category].amount += Math.abs(record.amount);
      acc[record.category].count += 1;
      return acc;
    }, {} as Record<string, { amount: number; count: number; percentage: number }>);

    Object.keys(categoryDistribution).forEach((categoryName) => {
      categoryDistribution[categoryName].percentage = records.length > 0
        ? (categoryDistribution[categoryName].count / records.length) * 100
        : 0;
    });
    
    return {
      total: records.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      approved: approved.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      pending: pending.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      paid: paid.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      rejected: rejected.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      todayTransactions: todayRecords.length,
      todayAmount: todayRecords.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      monthlyTransactions: currentMonthRecords.length,
      monthlyAmount: currentMonthRecords.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      budgetUtilization: 75,
      overdueAmount: 0,
      approvedCount: approved.length,
      pendingCount: pending.length,
      paidCount: paid.length,
      rejectedCount: rejected.length,
      categoryDistribution
    };
  }, [records, categories]);

  // Chart data processing
  const chartData = useMemo(() => {
    if (!records.length) return { 
      monthlyData: [], 
      categoryData: [],
      cashFlowData: [],
      statusTrendData: []
    };

    // Monthly data
    const monthlyMap: Record<string, { 
      income: number, 
      expense: number, 
      count: number,
      approved: number,
      pending: number,
      paid: number,
      rejected: number
    }> = {};
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get income and expense categories
    const incomeCategories = categories.filter(c => c.type === 'Income').map(c => c.name);
    const expenseCategories = categories.filter(c => c.type === 'Expense').map(c => c.name);
    const getCategoryType = (categoryName: string) => {
      const categoryType = categories.find(c => c.name === categoryName)?.type;
      if (categoryType) return categoryType;
      return categoryName.startsWith('Event Receipt') ? 'Income' : 'Expense';
    };
    
    records.forEach(record => {
      const date = new Date(record.date);
      const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      
      if (!monthlyMap[monthYear]) {
        monthlyMap[monthYear] = { 
          income: 0, 
          expense: 0, 
          count: 0,
          approved: 0,
          pending: 0,
          paid: 0,
          rejected: 0
        };
      }
      
      if (incomeCategories.includes(record.category) || getCategoryType(record.category) === 'Income') {
        monthlyMap[monthYear].income += Math.abs(record.amount);
      } else if (expenseCategories.includes(record.category) || getCategoryType(record.category) === 'Expense') {
        monthlyMap[monthYear].expense += Math.abs(record.amount);
      }
      
      // Status tracking
      switch (record.status) {
        case 'Approved':
          monthlyMap[monthYear].approved += Math.abs(record.amount);
          break;
        case 'Pending':
          monthlyMap[monthYear].pending += Math.abs(record.amount);
          break;
        case 'Paid':
          monthlyMap[monthYear].paid += Math.abs(record.amount);
          break;
        case 'Rejected':
          monthlyMap[monthYear].rejected += Math.abs(record.amount);
          break;
      }
      
      monthlyMap[monthYear].count++;
    });

    const monthlyData = Object.entries(monthlyMap)
      .map(([monthYear, data]) => {
        const [year, month] = monthYear.split('-');
        return {
          month: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
          income: data.income,
          expense: data.expense,
          net: data.income - data.expense,
          transactions: data.count,
          approved: data.approved,
          pending: data.pending,
          paid: data.paid,
          rejected: data.rejected,
          monthYear: monthYear
        };
      })
      .sort((a, b) => a.monthYear.localeCompare(b.monthYear))
      .slice(-12); // Last 12 months

    // Category data
    const categoryMap: Record<string, { amount: number, count: number, color: string, type: string }> = {};
    records.forEach(record => {
      if (!categoryMap[record.category]) {
        const category = categories.find(c => c.name === record.category);
        categoryMap[record.category] = {
          amount: 0,
          count: 0,
          color: category?.color || '#3b82f6',
          type: category?.type || (record.category.startsWith('Event Receipt') ? 'Income' : 'Expense')
        };
      }
      categoryMap[record.category].amount += Math.abs(record.amount);
      categoryMap[record.category].count++;
    });

    const categoryData = Object.entries(categoryMap)
      .map(([name, data]) => ({
        name,
        value: data.amount,
        count: data.count,
        color: data.color,
        type: data.type
      }))
      .sort((a, b) => b.value - a.value);

    // Cash Flow Data (cumulative)
    const cashFlowData = monthlyData.map((month, index, array) => {
      const cumulativeNet = array.slice(0, index + 1).reduce((sum, m) => sum + m.net, 0);
      const cumulativeIncome = array.slice(0, index + 1).reduce((sum, m) => sum + m.income, 0);
      const cumulativeExpense = array.slice(0, index + 1).reduce((sum, m) => sum + m.expense, 0);
      
      return {
        ...month,
        cumulativeNet,
        cumulativeIncome,
        cumulativeExpense,
        cashFlowRatio: month.income > 0 ? (month.net / month.income) * 100 : 0
      };
    });

    // Status Trend Data
    const statusTrendData = monthlyData.map(month => ({
      month: month.month,
      Approved: month.approved,
      Pending: month.pending,
      Paid: month.paid,
      Rejected: month.rejected,
      monthYear: month.monthYear
    }));

    return { 
      monthlyData, 
      categoryData,
      cashFlowData,
      statusTrendData
    };
  }, [records, categories]);

  // Recent transactions
  const recentTransactions = useMemo(() => {
    return [...records]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  }, [records]);

  // Top transactions
  const topTransactions = useMemo(() => {
    return [...records]
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
      .slice(0, 5);
  }, [records]);

  // Export functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blocked! Please allow popups to print.');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Accounting Dashboard Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
          .header h1 { color: #1e40af; margin: 0; }
          .header p { color: #6b7280; margin: 5px 0; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
          .stat-card { border: 1px solid #e5e7eb; padding: 15px; border-radius: 8px; background: #f9fafb; }
          .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
          .stat-card .value { font-size: 24px; font-weight: bold; color: #111827; margin: 5px 0; }
          .chart-container { page-break-inside: avoid; margin: 30px 0; }
          .chart-title { font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 15px; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; }
          .table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .table th { background: #f3f4f6; text-align: left; padding: 12px; border: 1px solid #d1d5db; font-weight: bold; }
          .table td { padding: 12px; border: 1px solid #d1d5db; }
          .footer { margin-top: 50px; text-align: center; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
          @media print {
            .no-print { display: none; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Accounting Dashboard Report</h1>
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Time Frame: ${activeTimeFrame === 'week' ? 'Last 7 Days' :
                         activeTimeFrame === 'month' ? 'This Month' :
                         activeTimeFrame === 'quarter' ? 'This Quarter' :
                         activeTimeFrame === 'year' ? 'This Year' :
                         'Custom Range'}</p>
        </div>

        <div class="stats-grid">
          <div class="stat-card">
            <h3>Total Revenue</h3>
              <div class="value">${formatCurrency(financialKPIs.totalRevenue)}</div>
            <p>Net income after expenses</p>
          </div>
          <div class="stat-card">
            <h3>Total Income</h3>
            <div class="value">${formatCurrency(financialKPIs.totalIncome)}</div>
            <p>Gross income</p>
          </div>
          <div class="stat-card">
            <h3>Total Expenses</h3>
            <div class="value">${formatCurrency(financialKPIs.totalExpense)}</div>
            <p>Total costs</p>
          </div>
          <div class="stat-card">
            <h3>Net Profit</h3>
            <div class="value">${formatCurrency(financialKPIs.netProfit)}</div>
            <p>Profit margin: ${financialKPIs.profitMargin.toFixed(1)}%</p>
          </div>
        </div>

        <div class="chart-container">
          <div class="chart-title">Recent Transactions</div>
          <table class="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${recentTransactions.map(t => `
                <tr>
                  <td>#${t.accountingId}</td>
                  <td>${new Date(t.date).toLocaleDateString()}</td>
                  <td>${t.description}</td>
                  <td>${formatCurrency(Math.abs(t.amount))}</td>
                  <td>${t.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="chart-container">
          <div class="chart-title">Summary</div>
          <p><strong>Total Transactions:</strong> ${records.length}</p>
          <p><strong>Average Transaction:</strong> ${formatCurrency(financialKPIs.avgTransaction)}</p>
          <p><strong>Reporting Period:</strong> ${new Date().toLocaleDateString()}</p>
        </div>

        <div class="footer">
          <p>Generated by Accounting Dashboard &copy; ${new Date().getFullYear()}</p>
          <p>This report is for informational purposes only.</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 1000);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleExportPDF = async () => {
    try {
      const element = dashboardRef.current;
      if (!element) {
        alert('Cannot generate PDF: Dashboard element not found');
        return;
      }

      const clone = element.cloneNode(true) as HTMLElement;
      const buttons = clone.querySelectorAll('button, .no-print');
      buttons.forEach(btn => btn.classList.add('hidden'));
      
      clone.style.width = '100%';
      clone.style.backgroundColor = 'white';
      
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);
      
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      document.body.removeChild(tempContainer);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`accounting-dashboard-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Date', 'Description', 'Category', 'Amount', 'Status', 'Payment Method'];
    const csvData = records.map(record => [
      record.accountingId,
      record.date,
      record.description,
      record.category,
      record.amount,
      record.status,
      record.paymentMethod
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3" ref={dashboardRef} id="dashboard-print">
      {/* Header with controls */}
      <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Financial Dashboard</h1>
            <p className="text-xs text-gray-600">
             Comprehensive financial overview and analytics 
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Controls */}
  <div className="flex items-center gap-2">
    <label htmlFor="from-date" className="w-12 text-xs text-gray-500 text-right">From</label>
    <input
      id="from-date"
      type="date"
      value={customDateRange.start}
      onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
      className="h-9 w-36 border rounded-md px-3 text-sm"
    />
    <span className="text-xs text-gray-500">To</span>
    <input
      id="to-date"
      type="date"
      value={customDateRange.end}
      onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
      className="h-9 w-36 border rounded-md px-3 text-sm"
    />
    <button
      onClick={() => { setActiveTimeFrame('custom'); fetchDashboardData(); }}
      className="h-9 bg-primary-600 hover:bg-primary-700 text-white px-3 rounded-md text-sm"
    >
      Apply
    </button>
  </div>

            <div className="hidden sm:block h-6 w-px bg-gray-200 mx-2" aria-hidden="true"></div>

            <div className="flex items-center">
              <ExportControls
                onPrint={handlePrint}
                onExportPDF={handleExportPDF}
                onExportCSV={handleExportCSV}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onRefresh={() => fetchDashboardData(true)}
                showFilters={showFilters}
              />
            </div>
          </div>
        </div>

        {/* Filters (always visible) */}
        <DashboardFilters 
          customDateRange={customDateRange}
          setCustomDateRange={setCustomDateRange}
          categories={categories}
          onApply={() => setActiveTimeFrame('custom')}
        />

        {/* First Row - 6 KPI Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          <StatCard
            title="Total Revenue"
            value={financialKPIs.totalRevenue}
            change="+12.5%"
            icon={<FaDollarSign className="h-4 w-4 text-green-500" />}
            color="text-green-500"
            subtitle="Net income"
            trend={financialKPIs.totalRevenue > 0 ? "up" : "down"}
          />
          <StatCard
            title="Total Income"
            value={financialKPIs.totalIncome}
            change="+8.2%"
            icon={<FaArrowTrendUp className="h-4 w-4 text-blue-500" />}
            color="text-blue-500"
            subtitle="Gross income"
            trend={financialKPIs.totalIncome > 0 ? "up" : "down"}
          />
          <StatCard
            title="Total Expenses"
            value={financialKPIs.totalExpense}
            change="-5.3%"
            icon={<FaRegMoneyBillAlt className="h-4 w-4 text-red-500" />}
            color="text-red-500"
            subtitle="Total costs"
            trend={financialKPIs.totalExpense > 0 ? "down" : "up"}
          />
          <StatCard
            title="Net Profit"
            value={financialKPIs.netProfit}
            change="+15.3%"
            icon={<FaBalanceScale className="h-4 w-4 text-purple-500" />}
            color="text-purple-500"
            subtitle={`Margin: ${financialKPIs.profitMargin.toFixed(1)}%`}
            trend={financialKPIs.netProfit > 0 ? "up" : "down"}
          />
          <StatCard
            title="Cash Flow"
            value={financialKPIs.cashFlow}
            change="+18.2%"
            icon={<FaExchangeAlt className="h-4 w-4 text-teal-500" />}
            color="text-teal-500"
            subtitle="Net cash position"
            trend={financialKPIs.cashFlow > 0 ? "up" : "down"}
          />
          <StatCard
            title="Avg Transaction"
            value={financialKPIs.avgTransaction}
            change="+3.7%"
            icon={<FaTachometerAlt className="h-4 w-4 text-amber-500" />}
            color="text-amber-500"
            subtitle="Average amount"
            trend={financialKPIs.avgTransaction > 0 ? "up" : "down"}
          />
        </div>

        {/* Second Row - 6 Additional Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
          <StatCard
            title="Total Transactions"
            value={financialKPIs.transactionCount}
            icon={<FaTable className="h-4 w-4 text-yellow-500" />}
            color="text-yellow-500"
            subtitle="All transactions"
          />
          <StatCard
            title="Monthly Cash Flow"
            value={dashboardStats.monthlyAmount}
            change="+8.2%"
            icon={<FaArrowTrendUp className="h-4 w-4 text-green-500" />}
            color="text-green-500"
            subtitle="This month"
            trend="up"
          />
          <StatCard
            title="Budget Utilization"
            value={`${dashboardStats.budgetUtilization}%`}
            icon={<FaPercentage className="h-4 w-4 text-indigo-500" />}
            color="text-indigo-500"
            subtitle="Of allocated budget"
          />
          <StatCard
            title="Paid Amount"
            value={dashboardStats.paid}
            change="+2.1%"
            icon={<FaWallet className="h-4 w-4 text-blue-500" />}
            color="text-blue-500"
            subtitle="Completed payments"
            trend="up"
          />
          <StatCard
            title="Pending Amount"
            value={dashboardStats.pending}
            change="+5.3%"
            icon={<FaClock className="h-4 w-4 text-amber-500" />}
            color="text-amber-500"
            subtitle="Awaiting approval"
            trend="up"
          />
          <StatCard
            title="Approved Amount"
            value={dashboardStats.approved}
            change="+1.8%"
            icon={<FaCheckCircle className="h-4 w-4 text-green-500" />}
            color="text-green-500"
            subtitle="Approved transactions"
            trend="up"
          />
        </div>

        {/* First Row Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Income vs Expense Trend */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Income vs Expense Trend</h3>
                <p className="text-xs text-gray-600">Monthly comparison with net profit</p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Income</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span>Expense</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" fontSize={10} />
                  <YAxis fontSize={10} domain={[0, 'auto']} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="income"
                    name="Income"
                    stroke="#059669"
                    fill="#10b981"
                    fillOpacity={0.28}
                    strokeWidth={1.5}
                    baseValue={0}
                  />
                  <Area
                    type="monotone"
                    dataKey="expense"
                    name="Expense"
                    stroke="#b91c1c"
                    fill="#ef4444"
                    fillOpacity={0.22}
                    strokeWidth={1.5}
                    baseValue={0}
                  />
                  <Area
                    type="monotone"
                    dataKey="net"
                    name="Net Profit"
                    stroke="#3b82f6"
                    fill="#bfdbfe"
                    fillOpacity={0.24}
                    strokeWidth={1.5}
                    baseValue={0}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Category Distribution</h3>
                <p className="text-xs text-gray-600">Spending by category (Top 8)</p>
              </div>
              <FaChartPie className="h-4 w-4 text-gray-400" />
            </div>
            <div className="h-64">
              {chartData.categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData.categoryData.slice(0, 8)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent && percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                      outerRadius={80}
                      innerRadius={30}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {chartData.categoryData.slice(0, 8).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => [formatCurrency(Number(value)), 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No category data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Row Charts - New Detailed Area Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Cumulative Cash Flow */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Cumulative Cash Flow</h3>
                <p className="text-xs text-gray-600">Running total of net cash position</p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span>Cumulative</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.cashFlowData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                            <p className="font-semibold text-gray-900 text-sm mb-2">{label}</p>
                            {payload.map((entry: any, index: number) => (
                              <div key={index} className="flex items-center justify-between mb-1">
                                <span className="text-xs text-gray-600">{entry.name}:</span>
                                <span className="text-xs font-medium text-gray-900">
                                  {formatCurrency(entry.value || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="cumulativeNet"
                    name="Cumulative Net"
                    stroke="#3b82f6"
                    fill="#93c5fd"
                    fillOpacity={0.3}
                    strokeWidth={1.5}
                  />
                  <Line
                    type="monotone"
                    dataKey="net"
                    name="Monthly Net"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Transaction Status Trend */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Transaction Status Trend</h3>
                <p className="text-xs text-gray-600">Monthly status distribution</p>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Approved</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span>Pending</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.statusTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 min-w-48">
                            <p className="font-semibold text-gray-900 text-sm mb-2">{label}</p>
                            {payload.map((entry: any, index: number) => (
                              <div key={index} className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-2 h-2 rounded" 
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-xs text-gray-600">{entry.name}:</span>
                                </div>
                                <span className="text-xs font-medium text-gray-900">
                                  {formatCurrency(entry.value || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Approved"
                    name="Approved"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                  />
                  <Area
                    type="monotone"
                    dataKey="Pending"
                    name="Pending"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                  />
                  <Area
                    type="monotone"
                    dataKey="Paid"
                    name="Paid"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                  />
                  <Area
                    type="monotone"
                    dataKey="Rejected"
                    name="Rejected"
                    stroke="#ef4444"
                    fill="#ef4444"
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <RecentTransactionsTable transactions={recentTransactions} />
          <TopTransactionsList transactions={topTransactions} />
        </div>

        {/* Summary Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-500">Reporting Period</p>
              <p className="font-medium text-gray-900 text-sm">
                {activeTimeFrame === 'week' ? 'Last 7 Days' :
                 activeTimeFrame === 'month' ? 'This Month' :
                 activeTimeFrame === 'quarter' ? 'This Quarter' :
                 activeTimeFrame === 'year' ? 'This Year' :
                 'Custom Range'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Data Last Updated</p>
              <p className="font-medium text-gray-900 text-sm">
                {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Total Records Processed</p>
              <p className="font-medium text-gray-900 text-sm">{records.length} transactions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingDashboard;
